/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';

import AvisoDeCicloForaDaTela from 'components/ui/aviso-de-ciclo-fora-da-tela';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import { useAuth } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { plainText } from 'pages/Dashboard/components/recent-recognitions.format';
import { rotuloNivel } from 'pages/Extrato/perfil-calculos';
import { getMissaoEquipePublic, getMissaoGestorPublic } from 'services/achievement.service';
import { getKPIPublicHistoryByTime, getPlayerInfo } from 'services/dashboard.service';
import { getPublicProfile } from 'services/profile.service';
import { FormatNumber } from 'utils/helpers';
import { GetBehaviorLabel, GetTaskLevelLabel } from 'utils/strings';

import { corDoFunci, corDoStatus, dataCompleta, estatisticasDoPerfil, iniciaisDoFunci, rotuloDaMoeda } from '../feed-calculos';
import AvatarDoFunci from './AvatarDoFunci';
import { FOCO_SOBRE_AZUL, FOCO_VISIVEL } from 'utils/foco';

/** Ciclos do seletor: o Feed público só existe a partir do 2S2023. */
const INICIO_DO_FEED_PUBLICO = 1690772400000;

type Aba = 'reconhecimentos' | 'realizacoes';

interface PerfilDoColegaProps {
  matricula: string;
  /** Posição do funci na lista, para manter a cor das iniciais. */
  posicao: number;
  onVoltar: () => void;
}

const VOLTAR = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--brand-yellow)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

function ehGestor(info: any) {
  return (
    info?.tipo_ro === 'GERENCIAL' ||
    info?.funcao === 'DIRETOR' ||
    (Array.isArray(info?.mad_dupla) && info.mad_dupla.some((md: any) => md?.tipo_ro === 'GERENCIAL'))
  );
}

export default function PerfilDoColega({ matricula, posicao, onVoltar }: PerfilDoColegaProps) {
  const { user, realCycle } = useAuth();
  const rede = !!user?.extra?.rede;

  const ciclos = useMemo(
    () => (realCycle ?? []).filter((ciclo: any) => ciclo?.date_start >= INICIO_DO_FEED_PUBLICO),
    [realCycle],
  );
  // CIC-10 a CIC-12 (no lugar de AJH-36): o ciclo do perfil é o ciclo do app; trocar aqui troca o do app.
  const { ciclo, foraDaTela, maisRecente, trocar: trocarCiclo } = useCicloDaTela(ciclos);
  const cicloId = ciclo?._id;

  const [perfil, setPerfil] = useState<any>();
  const [info, setInfo] = useState<any>();
  const [reconhecimentos, setReconhecimentos] = useState<any[]>([]);
  const [realizacoes, setRealizacoes] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<Aba>('reconhecimentos');

  useEffect(() => {
    if (!ciclo) return;
    const alvo = ciclo;
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      try {
        const [perfilPublico, playerInfo, recebidos] = await Promise.all([
          getPublicProfile(matricula, alvo._id),
          getPlayerInfo(matricula),
          getKPIPublicHistoryByTime(matricula, alvo.date_start, alvo.date_end, alvo._id),
        ]);
        const equipe = await getMissaoEquipePublic(matricula, alvo.date_start, alvo.date_end, alvo._id);
        const gestor = ehGestor(playerInfo)
          ? await getMissaoGestorPublic(matricula, alvo.date_start, alvo.date_end, alvo._id)
          : [];

        if (!ativo) return;
        setPerfil(perfilPublico);
        setInfo(playerInfo);
        setReconhecimentos((recebidos ?? []).filter((item: any) => !item.cancelled));
        setRealizacoes([...(gestor ?? []), ...(equipe ?? [])]);
      } catch (e) {
        if (ativo) {
          setReconhecimentos([]);
          setRealizacoes([]);
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [matricula, ciclo?._id]);

  const nome = perfil?.name ?? '';
  const nivel = rotuloNivel(perfil?.level);
  const pontosDaRealizacao = (item: any) => item.totalMissao ?? item.total ?? 0;
  const estatisticas = estatisticasDoPerfil(
    reconhecimentos,
    realizacoes.map(item => ({ total: pontosDaRealizacao(item) })),
  );
  const funcis = perfil?.grupo_total ?? info?.grupo?.total;
  const abas: { chave: Aba; rotulo: string }[] = rede
    ? [{ chave: 'reconhecimentos', rotulo: 'Reconhecimentos recebidos' }]
    : [
        { chave: 'reconhecimentos', rotulo: 'Reconhecimentos recebidos' },
        { chave: 'realizacoes', rotulo: 'Participação em realizações' },
      ];

  return (
    <div>
      <div className="mb-[22px] flex items-center gap-3.5">
        <button
          type="button"
          onClick={onVoltar}
          aria-label="Voltar para o feed"
          className={`flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-brand-blue ${FOCO_VISIVEL}`}
        >
          {VOLTAR}
        </button>
        <h1 tabIndex={0} className={`m-0 font-display text-[28px] font-extrabold leading-[normal] text-brand-blue-dark ${FOCO_VISIVEL}`}>
          Perfil de {nome}
        </h1>
      </div>

      <section
        aria-label={`Resumo de ${nome}`}
        className="flex flex-wrap items-center gap-[26px] rounded-2xl bg-brand-blue px-[30px] py-[26px] text-brand-white"
      >
        <AvatarDoFunci
          nomeDeGuerra={info?.nome_guerra ?? nome}
          cor={corDoFunci(posicao)}
          tamanho={120}
          fonte="text-[38px]"
          dados={perfil ? { ...perfil, ...perfil.extra } : undefined}
        />
        <div className="min-w-[280px] flex-1">
          <p tabIndex={0} className={`m-0 font-display text-[26px] font-extrabold leading-[1.1] text-brand-yellow ${FOCO_SOBRE_AZUL}`}>
            {nome} <span className="text-[18px] font-semibold text-brand-white">({matricula})</span>
          </p>
          {/* SPEC_DEVIATION: nível em branco e textos sem transparência (4,5:1 sobre o azul); prefixo sobre azul-escuro. */}
          <p tabIndex={0} className={`m-0 mt-2.5 flex flex-wrap items-center gap-2.5 text-[13px] ${FOCO_SOBRE_AZUL}`}>
            {nivel && (
              <>
                <span>
                  Nível: <b className="text-brand-white">{nivel}</b>
                </span>
                <span aria-hidden="true">/</span>
              </>
            )}
            <span>
              {info?.funcao} {info?.diretoria}
            </span>
            {funcis ? <span>({funcis} funcis)</span> : null}
            {info?.ro && (
              <span className="rounded-full bg-brand-blue-dark px-[13px] py-[5px] font-display text-[11px] font-bold text-brand-yellow">
                {info.ro}
              </span>
            )}
          </p>
          <dl className="m-0 mt-4 flex flex-wrap gap-[22px]">
            <div tabIndex={0} className={`flex items-center gap-[9px] ${FOCO_SOBRE_AZUL}`}>
              <dt className="text-[11px] font-normal leading-[1.2]">
                Reconhecimentos
                <br />
                recebidos:
              </dt>
              <dd className="m-0 font-display text-[24px] font-bold leading-[normal] text-brand-white">{estatisticas.reconhecimentos}</dd>
            </div>
            {!rede && (
              <>
                <div tabIndex={0} className={`flex items-center gap-[9px] ${FOCO_SOBRE_AZUL}`}>
                  <dt className="text-[11px] font-normal leading-[1.2]">
                    Realizações
                    <br />
                    no ciclo:
                  </dt>
                  <dd className="m-0 font-display text-[24px] font-bold leading-[normal] text-brand-cyan-light">{estatisticas.realizacoes}</dd>
                </div>
                <div tabIndex={0} className={`flex items-center gap-[9px] ${FOCO_SOBRE_AZUL}`}>
                  <dt className="text-[11px] font-normal leading-[1.2]">
                    Pontos em
                    <br />
                    realizações:
                  </dt>
                  <dd className="m-0 font-display text-[24px] font-bold leading-[normal] text-brand-yellow">{estatisticas.pontos}</dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </section>

      <div className="mb-3.5 mt-[26px] flex flex-wrap items-center justify-between gap-5">
        <div className="flex items-center gap-2.5">
          <h2 tabIndex={0} className={`m-0 font-display text-[19px] font-bold leading-[normal] text-brand-blue-dark ${FOCO_VISIVEL}`}>
            Feed Joga Junto
          </h2>
          <span aria-hidden="true" className="text-content-soft">
            |
          </span>
          {/* SPEC_DEVIATION: `brand-blue-dark` (design `brand-blue`, 4,35:1 em 17px). */}
          <span tabIndex={0} className={`font-display text-[17px] font-bold text-brand-blue-dark ${FOCO_VISIVEL}`}>
            Ciclo {ciclo?.title ?? foraDaTela?.title}
          </span>
        </div>
        <SeletorDeCiclo ciclos={ciclos} valor={cicloId} onChange={trocarCiclo} className="w-[264px] max-w-full" />
      </div>

      {foraDaTela && <AvisoDeCicloForaDaTela ciclo={foraDaTela} disponivel={maisRecente} onVer={trocarCiclo} />}

      <div hidden={!!foraDaTela} className="mb-4 flex gap-2.5">
        {abas.map(item => (
          <button
            key={item.chave}
            type="button"
            aria-pressed={aba === item.chave}
            onClick={() => setAba(item.chave)}
            // SPEC_DEVIATION: aba ativa em `brand-blue-dark` (design `brand-blue`, 4,44:1 com o texto amarelo).
            className={`flex min-h-[62px] flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border-0 px-4 py-3 font-display text-[14px] font-bold shadow-jj-01 transition hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(0,45,75,0.12)] motion-reduce:hover:translate-y-0 ${FOCO_VISIVEL} ${
              aba === item.chave ? 'bg-brand-blue-dark text-brand-yellow' : 'bg-brand-white text-brand-blue'
            }`}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      {!foraDaTela && aba === 'reconhecimentos' && (
        <section aria-labelledby="titulo-recebidos">
          <h3
            id="titulo-recebidos"
            tabIndex={0}
            className={`m-0 mb-3.5 font-display text-[16px] font-bold leading-[normal] text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            {/* SPEC_DEVIATION: contador em `brand-blue-dark` (design `brand-pink`, 2,37:1). */}
            Reconhecimentos recebidos <span className="text-brand-blue-dark">({estatisticas.reconhecimentos})</span>
          </h3>
          {!carregando && reconhecimentos.length === 0 ? (
            <p tabIndex={0} className={`m-0 rounded-xl bg-brand-white p-[34px] text-center text-[13px] text-content shadow-jj-01 ${FOCO_VISIVEL}`}>
              Nenhum reconhecimento recebido neste ciclo.
            </p>
          ) : (
            <ul className="m-0 grid list-none grid-cols-1 gap-3.5 p-0 md:grid-cols-2">
              {reconhecimentos.map((item, indice) => {
                const autor = (item.sender_name ?? '').split(' ')[0].toUpperCase();
                return (
                  <li key={item._id} tabIndex={0} className={`flex flex-col gap-3 rounded-xl bg-brand-white px-[22px] py-5 shadow-jj-01 ${FOCO_VISIVEL}`}>
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-display text-[14px] font-bold text-brand-navy ${corDoFunci(indice)}`}
                      >
                        {iniciaisDoFunci(autor)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="m-0 text-[13px]">
                          <b className="font-display text-content">{autor}</b>{' '}
                          <span className="text-content-soft">({item.sender})</span> enviou:
                        </p>
                        <p className="m-0 mt-1 text-[11px] text-content-soft">{dataCompleta(item.time)}</p>
                      </div>
                      <span className="inline-flex items-center gap-[7px] whitespace-nowrap rounded-full bg-brand-yellow-light px-3 py-[5px] text-[10px] font-bold">
                        {[rotuloDaMoeda(item.coin), GetBehaviorLabel(item.behavior)]
                          .filter(Boolean)
                          .flatMap((parte, i) => (i ? [' · ', parte] : [parte]))
                          .map((parte, i) => (
                            <span key={i}>{parte.trim()}</span>
                          ))}
                      </span>
                    </div>
                    <div className="border-t border-solid border-[#E5E7EB] pt-3">
                      <p className="m-0 mb-[5px] text-[11px] font-semibold text-brand-blue">Mensagem recebida:</p>
                      <p className="m-0 text-[12.5px] leading-[1.6] text-content">{plainText(item.message ?? '')}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {!foraDaTela && aba === 'realizacoes' && !rede && (
        <section aria-label="Participação em realizações">
          {!carregando && realizacoes.length === 0 ? (
            <p tabIndex={0} className={`m-0 rounded-xl bg-brand-white p-[34px] text-center text-[13px] text-content shadow-jj-01 ${FOCO_VISIVEL}`}>
              Nenhuma realização neste ciclo.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
              {realizacoes.map(item => {
                const status = item.status ?? item.task?.status;
                return (
                  <li key={item._id} tabIndex={0} className={`flex flex-wrap items-center gap-5 rounded-xl bg-brand-white px-6 py-5 shadow-jj-01 ${FOCO_VISIVEL}`}>
                    <div className="min-w-0 flex-[2]">
                      <p className="m-0 mb-[5px] text-[10px] text-content-soft">Nome da realização</p>
                      <p className="m-0 text-[13px] font-semibold leading-[1.45]">{item.titulo ?? item.task?.title}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 mb-[5px] text-[10px] text-content-soft">Status</p>
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.04em] text-brand-navy ${corDoStatus(status)}`}
                      >
                        {GetTaskLevelLabel(status ?? '')}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 mb-[5px] text-[10px] text-content-soft">Grupo de trabalho</p>
                      <p className="m-0 text-[12px] font-semibold">{item.nome_uor ?? info?.nome_uor}</p>
                    </div>
                    {/* SPEC_DEVIATION: fundo `brand-blue-dark` (design `brand-blue`, 4,44:1 com o texto amarelo). */}
                    <p className="m-0 shrink-0 rounded-full bg-brand-blue-dark px-[18px] py-[9px] text-center text-brand-yellow">
                      <span className="block font-display text-[17px] font-extrabold leading-none">
                        {FormatNumber(pontosDaRealizacao(item), false)}
                      </span>
                      <span className="mt-[3px] block text-[9px] uppercase tracking-[0.06em]">Pontos</span>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
